<template>
  <v-dialog v-model="showModal" max-width="600px" persistent>
    <v-card>
      <v-card-title>
        <span class="text-h5">Edit User</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="formRef">
          <v-text-field
            v-model="localUser.email"
            label="Email"
            type="email"
            variant="outlined"
            :rules="[
              v => !!v || 'Email is required',
              v => /.+@.+\..+/.test(v) || 'Email must be valid'
            ]"
            required
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="localUser.first_name"
            label="First Name"
            variant="outlined"
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="localUser.last_name"
            label="Last Name"
            variant="outlined"
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="localUser.organization"
            label="Organization"
            variant="outlined"
            class="mb-2"
          ></v-text-field>

          <v-select
            v-model="localUser.school"
            :items="schoolOptions"
            label="School"
            variant="outlined"
            clearable
            class="mb-2"
          ></v-select>

          <v-select
            v-model="localUser.role"
            :items="roleOptions"
            label="Role"
            variant="outlined"
            :rules="[v => !!v || 'Role is required']"
            required
            :disabled="!canEditRole || isSelfEdit"
            :hint="getRoleHint()"
            persistent-hint
            class="mb-2"
          ></v-select>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-1 mb-3">Password Reset (Optional)</div>
          <p class="text-caption text-grey mb-3">Leave blank to keep current password</p>

          <v-text-field
            v-model="localUser.password"
            label="New Password"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            :rules="[
              v => !v || v.length >= 8 || 'Password must be at least 8 characters'
            ]"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showPassword = !showPassword"
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="localUser.confirmPassword"
            label="Confirm New Password"
            :type="showConfirmPassword ? 'text' : 'password'"
            variant="outlined"
            :rules="[
              v => !localUser.password || !!v || 'Please confirm password',
              v => !localUser.password || v === localUser.password || 'Passwords must match'
            ]"
            :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
          ></v-text-field>

          <v-alert v-if="error" type="error" variant="tonal" class="mt-4">
            {{ error }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" @click="closeModal" :disabled="submitting">
          Cancel
        </v-btn>
        <v-btn color="primary" @click="submitUser" :loading="submitting">
          Update User
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { ref, computed, watch } from 'vue'
import { useAuthStore } from '@/store/auth'
import { OperationCanceledException } from 'typescript'

const authStore = useAuthStore()

interface User {
  _id?: string
  id?: string
  email: string
  first_name?: string
  last_name?: string
  first?: string
  last?: string
  organization?: string
  school?: string | null
  role: string
}

interface School {
  id: string
  name: string
}

interface Props {
  show: boolean
  user: User | null
  schools: School[]
  submitting?: boolean
  error?: string
  canEditRole?: boolean
  isSelfEdit?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  submitting: false,
  error: '',
  canEditRole: true,
  isSelfEdit: false
})

const emit = defineEmits(['close', 'submit'])

const formRef = ref<any>(null)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

// Local editable copy of the user
const localUser = ref({
  email: '',
  first_name: '',
  last_name: '',
  organization: '',
  school: null as string | null,
  role: 'user',
  password: '',
  confirmPassword: ''
})

const showModal = computed({
  get: () => props.show,
  set: (value) => {
    if (!value) emit('close')
  }
})

const schoolOptions = computed(() => {
  return props.schools.map(school => ({
    title: school.name,
    value: school.id
  }))
})

// Get available roles from auth store with disabled state for roles above current user
const roleOptions = computed(() => {
  // Use the perceived role (authUser.role, which is overridden during "view as")
  const currentRole = authStore.authUser.role
  const currentUserLevel = authStore.roleHierarchy[currentRole] || 0

  // Get all roles from hierarchy
  const allRoles = Object.keys(authStore.roleHierarchy)
    .filter(role => role !== 'pending' && role !== 'root') // Exclude pending and root from options
    .map(role => {
      const roleLevel = authStore.roleHierarchy[role] || 0
      return {
        title: authStore.roleDisplayNames[role] || role,
        value: role,
        disabled: roleLevel > currentUserLevel // Disable roles above current user's level
      }
    })
    .sort((a, b) => {
      const levelA = authStore.roleHierarchy[a.value] || 0
      const levelB = authStore.roleHierarchy[b.value] || 0
      return levelB - levelA // Sort by level descending
    })

    for (const roleOption of allRoles) {
      if(roleOption.disabled){
        //remove disabled roles from the list
        const index = allRoles.indexOf(roleOption)
        if (index > -1) {
          allRoles.splice(index, 1)
        }
      }
    }

  return allRoles
})

const getRoleHint = () => {
  if (props.isSelfEdit) {
    return 'You cannot change your own role to prevent lockout'
  }
  if (!props.canEditRole) {
    return 'Only admins can change user roles'
  }
  return ''
}

const closeModal = () => {
  // Reset local copy
  localUser.value = {
    email: '',
    first_name: '',
    last_name: '',
    organization: '',
    school: null,
    role: 'user',
    password: '',
    confirmPassword: ''
  }
  showPassword.value = false
  showConfirmPassword.value = false
  emit('close')
}

const submitUser = async () => {
  const { valid } = await formRef.value.validate()

  if (!valid) {
    return
  }
  const userData: any = {
    email: localUser.value.email,
    first_name: localUser.value.first_name,
    last_name: localUser.value.last_name,
    organization: localUser.value.organization,
    school: localUser.value.school,
    role: localUser.value.role
  }

  // Only include password if it's being changed
  if (localUser.value.password) {
    userData.password = localUser.value.password
  }

  emit('submit', userData)
}

// Watch for user prop changes and populate local copy
watch(() => props.user, (newUser) => {
  if (newUser) {
    console.log('Loading user into edit modal:', newUser)
    // Create a fresh copy of the user data
    localUser.value = {
      email: newUser.email || '',
      first_name: newUser.first_name || newUser.first || '',
      last_name: newUser.last_name || newUser.last || '',
      organization: newUser.organization || '',
      school: newUser.school || null,
      role: newUser.role || 'user',
      password: '',
      confirmPassword: ''
    }
    console.log('Local user copy created:', localUser.value)
  }
}, { immediate: true, deep: true })
</script>

<style scoped>
/* Add any custom styles if needed */
</style>
